<template>
  <Overlay :open="open" :aria-label="product ? product.name : 'Product'" @close="$emit('close')">
    <!-- `flex-1` + `min-h-0` rather than `h-full`: the Overlay panel is bounded by max-height with
         no definite height, so `height: 100%` here would resolve to auto and nothing could scroll. -->
    <div v-if="product" class="flex flex-col flex-1 w-full min-h-0 bg-white" data-testid="product-modal">
      <div class="relative flex-shrink-0">
        <ProductModalPhotos
          v-if="product.imageUrls.length"
          :key="product.slug"
          :urls="product.imageUrls"
          :alt="product.name"
        />
        <button
          type="button"
          aria-label="Close"
          data-testid="product-modal-close"
          class="absolute z-10 top-4 right-4 flex items-center justify-center w-[38px] h-[38px] rounded-full bg-white border-2 border-brand-500 text-brand-600 shadow-md"
          @click="$emit('close')"
        >
          <XIcon class="w-5 h-5" />
        </button>
      </div>

      <!-- `min-h-0` is load-bearing: a flex item defaults to `min-height: auto`, so without it this
           grows to its content height and `overflow-y-auto` never engages - the long modifier list
           then pushes the sticky footer off the bottom of the screen instead of scrolling. -->
      <div ref="body" class="flex-1 min-h-0 overflow-y-auto" data-testid="product-modal-body">
        <!-- The close button is absolutely positioned inside the hero wrapper, which collapses to
             nothing when a product has no photo - so it then floats over this copy instead of over
             the image, and a long title wraps underneath it. Reserve its width (16px inset + 38px
             button + a gap) whenever there is no hero to sit on. -->
        <div class="px-6 pt-5 pb-4" :class="{ 'pr-16': !product.heroUrl }" data-testid="product-modal-header">
          <div v-if="product.chips.length" class="flex flex-wrap gap-2 mb-3">
            <Chip v-for="chip in product.chips" :key="chip.text" :label="chip.text" size="md" />
          </div>

          <h2 class="text-xl font-bold tracking-tight text-gray-brand-900" data-testid="product-modal-title">
            {{ product.name }}
          </h2>

          <p
            v-if="product.description"
            class="mt-3 text-sm leading-5 whitespace-pre-line text-gray-brand-700"
            data-testid="product-modal-description"
          >
            {{ product.description }}
          </p>

          <!-- The collapsed card used to carry this in V1 and never in V2. It belongs with the
               rest of the detail now that the card is a summary, and putting it here is what makes
               the two versions show the same thing. -->
          <ProductNutritionalInformation
            v-if="nutrition"
            :nutritional-information="nutrition"
            class="mt-3"
            data-testid="product-modal-nutrition"
          />
        </div>

        <section
          v-for="group in product.modifierGroups"
          :key="group.id"
          :data-testid="`modifier-group-${group.id}`"
          :data-missing="isMissing(group) ? 'true' : null"
          :aria-labelledby="`modifier-group-heading-${group.id}`"
        >
          <button
            type="button"
            :data-testid="`modifier-group-header-${group.id}`"
            :aria-expanded="isExpanded(group) ? 'true' : 'false'"
            class="flex items-center justify-between w-full px-6 py-3 text-left"
            :class="isMissing(group) ? 'bg-red-50 ring-2 ring-inset ring-red-500' : 'bg-gray-brand-200'"
            @click="toggleGroup(group)"
          >
            <span
              :id="`modifier-group-heading-${group.id}`"
              :class="{ 'required-nudge': nudgedGroupId === group.id }"
              @animationend="nudgedGroupId = null"
            >
              <span class="block text-[15px] font-bold leading-5 tracking-tight text-gray-brand-900">
                {{ group.name }}
              </span>
              <span
                :data-testid="`modifier-group-rule-${group.id}`"
                class="flex items-center gap-1 text-[13px] leading-[18px]"
                :class="isMissing(group) ? 'font-semibold text-red-700' : 'text-gray-brand-600'"
              >
                <ExclamationCircleIcon v-if="isMissing(group)" class="flex-shrink-0 w-4 h-4" aria-hidden="true" />
                {{ groupSubtitle(group) }}
              </span>
            </span>
            <span class="flex items-center gap-3">
              <Chip
                v-if="group.required"
                label="Required"
                :variant="isMissing(group) ? 'error' : 'required'"
                size="md"
              />
              <ChevronUpIcon v-if="isExpanded(group)" class="w-4 h-4 text-gray-brand-600" />
              <ChevronDownIcon v-else class="w-4 h-4 text-gray-brand-600" />
            </span>
          </button>

          <div
            v-show="isExpanded(group)"
            :role="group.mode === SELECT_ONE ? 'radiogroup' : 'group'"
            :aria-labelledby="`modifier-group-heading-${group.id}`"
          >
            <template v-if="group.mode === QUANTITY">
              <div
                v-for="(option, index) in group.options"
                :key="option.id"
                class="flex items-center justify-between px-6 py-4 bg-white"
                :class="{ 'border-b border-gray-brand-300': index < group.options.length - 1 }"
                :data-testid="`option-${option.id}`"
              >
                <span>
                  <span class="block text-[15px] font-medium leading-5 text-gray-brand-900">{{ option.name }}</span>
                  <span v-if="option.description" class="block text-[13px] leading-[18px] text-gray-brand-600">
                    {{ option.description }}
                  </span>
                </span>
                <span class="flex items-center gap-3">
                  <span v-if="optionPriceLabel(option)" class="text-sm font-bold text-gray-brand-700">
                    {{ optionPriceLabel(option) }}
                  </span>
                  <QuantityStepper
                    :model-value="quantityFor(group, option)"
                    :max="optionMax(group, option)"
                    :disabled="option.soldOut"
                    @update:model-value="setOptionQuantity(group, option, $event)"
                  />
                </span>
              </div>
            </template>

            <template v-else>
              <RadioRow
                v-for="(option, index) in group.options"
                :key="option.id"
                :data-testid="`option-${option.id}`"
                :label="option.name"
                :sublabel="option.description"
                :price="optionPriceLabel(option)"
                :selected="isSelected(group, option)"
                :disabled="option.soldOut"
                :multiple="group.mode === SELECT_MANY"
                :show-divider="index < group.options.length - 1"
                @select="chooseOption(group, option)"
              />
            </template>
          </div>
        </section>
      </div>

      <p
        v-if="missingChoicesMessage"
        role="alert"
        data-testid="product-modal-missing"
        class="flex items-center flex-shrink-0 gap-2 px-4 py-2 text-sm font-semibold text-red-700 border-t border-red-200 bg-red-50 sm:px-5"
      >
        <ExclamationCircleIcon class="flex-shrink-0 w-5 h-5" aria-hidden="true" />
        {{ missingChoicesMessage }}
      </p>

      <!-- The stepper is a fixed 144px (38 + 52 + 38 plus its gaps), so on a 360px phone the button
           was left about 120px of inner width for "Add to cart" and the total together - and the
           label wrapped onto a second line, which grew the button past the row it sits in. Tighter
           padding and a smaller label below `sm` buy back the ~30px that costs; `truncate` is the
           backstop on the narrowest devices, where an ellipsis is still better than a CTA that
           reflows. The total never shrinks: it is the number the customer is agreeing to. -->
      <div
        class="flex items-center flex-shrink-0 gap-3 px-4 py-3 bg-white border-t border-gray-brand-300 sm:gap-4 sm:px-5"
      >
        <div class="flex flex-col items-center flex-shrink-0">
          <QuantityStepper
            v-model="quantity"
            :min="minQuantity"
            :max="maxQuantity"
            :step="product.quantityStep"
            :floor="startingQuantity"
            :show-trash-at-min="editing"
          />
          <span
            v-if="quantityUnitLabel"
            data-testid="product-modal-quantity-unit"
            class="w-0 min-w-full mt-0.5 text-xs leading-4 text-center truncate text-gray-brand-600"
          >
            {{ quantityUnitLabel }}
          </span>
        </div>
        <!-- Never disabled or dimmed for a missing choice: a dead-looking button tells the customer
             nothing, and the group may be off screen or collapsed. The press reaches `add`, which
             names the problem and sends them to the group. Only closed ordering, which has nothing
             to send them to, disables it outright. -->
        <button
          type="button"
          data-testid="product-modal-add"
          :disabled="orderingClosed"
          :data-missing-choices="missingChoiceCount"
          class="flex items-center justify-between flex-1 min-w-0 gap-2 h-12 px-3 text-sm font-semibold text-white border rounded-lg bg-brand-500 border-brand-600 sm:px-5 sm:text-base"
          :class="{ 'opacity-50 cursor-not-allowed': orderingClosed }"
          @click="add"
        >
          <span class="truncate">{{ ctaLabel }}</span>
          <span v-if="product.showPrices" data-testid="product-modal-total" class="flex-shrink-0 whitespace-nowrap">
            {{ formattedTotal }}
          </span>
        </button>
      </div>
    </div>
  </Overlay>
</template>

<script>
import { ChevronDownIcon, ChevronUpIcon, ExclamationCircleIcon, XIcon } from '@heroicons/vue/solid'
import Overlay from '@/components/Molecules/Overlay.vue'
import Chip from '@/components/Molecules/Chip.vue'
import RadioRow from '@/components/Molecules/RadioRow.vue'
import QuantityStepper from '@/components/Molecules/QuantityStepper.vue'
import ProductNutritionalInformation from '@/components/Product/ProductNutritionalInformation'
import ProductModalPhotos from '@/components/Product/ProductModalPhotos.vue'
import { formatPrice } from '@/composables/useCartMutation'
import {
  combinedNutrition,
  groupSelectionTotal,
  runningTotalCents,
  snapQuantity,
  unsatisfiedModifierGroups,
  SELECT_ONE,
  SELECT_MANY,
  QUANTITY
} from '@/models/productViewModel'
import { fittedLineQuantity } from '@/utils/cartLineQuantity'

export default {
  name: 'ProductModal',

  components: {
    Overlay,
    Chip,
    RadioRow,
    QuantityStepper,
    ProductNutritionalInformation,
    ProductModalPhotos,
    ChevronDownIcon,
    ChevronUpIcon,
    ExclamationCircleIcon,
    XIcon
  },

  props: {
    open: { type: Boolean, default: false },
    // A normalized product from @/models/productViewModel (fromV1 or fromV2). This component never
    // learns which checkout version produced it.
    product: { type: Object, default: null },
    currency: { type: String, default: 'USD' },
    // Re-opening an already-configured card pre-fills the modal from the existing cart line.
    initialSelection: { type: Object, default: null },
    initialQuantity: { type: Number, default: 1 },
    // True when this product already has a cart line, which changes the CTA wording.
    editing: { type: Boolean, default: false },
    orderingClosed: { type: Boolean, default: false }
  },

  emits: ['close', 'add'],

  data() {
    return {
      SELECT_ONE,
      SELECT_MANY,
      QUANTITY,
      selection: {},
      quantity: 1,
      collapsedGroupIds: [],
      addRefused: false,
      nudgedGroupId: null
    }
  },

  computed: {
    totalCents() {
      if (!this.product) return 0
      return runningTotalCents(this.product, this.selection, this.quantity)
    },

    formattedTotal() {
      return formatPrice(this.totalCents, this.currency)
    },

    nutrition() {
      if (!this.product) return null
      return combinedNutrition(this.product, this.selection)
    },

    unsatisfiedGroups() {
      if (!this.product) return []
      return unsatisfiedModifierGroups(this.product, this.selection)
    },

    // A merchant selling in fives sets starting_quantity 5 / step 5, and the backend rounds a
    // smaller number up - so opening on 1 would quote one price and charge another.
    startingQuantity() {
      return Math.max(1, Number(this.product && this.product.startingQuantity) || 0)
    },

    // Editing an existing line must be able to reach zero. The card no longer carries a quantity
    // stepper of its own, so with a floor of the starting quantity there would be no way at all to
    // remove a line from the cart - both carts already treat a quantity of 0 as a removal.
    minQuantity() {
      return this.editing ? 0 : this.startingQuantity
    },

    maxQuantity() {
      const max = this.product && this.product.maxPerOrder
      if (max == null) return null
      const rules = { step: this.product.quantityStep, floor: this.startingQuantity, max }
      return fittedLineQuantity(max, rules) ?? max
    },

    isRemoving() {
      return this.editing && this.quantity === 0
    },

    // Removing a line does not need its required groups satisfied.
    missingChoiceCount() {
      return this.isRemoving ? 0 : this.unsatisfiedGroups.length
    },

    canAdd() {
      if (!this.product || this.orderingClosed) return false
      return this.missingChoiceCount === 0
    },

    // Flagged only after a refused press, so the modal does not open on a wall of errors for
    // choices the customer has not had a chance to make yet.
    missingGroupIds() {
      if (!this.addRefused || this.isRemoving) return []
      return this.unsatisfiedGroups.map((group) => group.id)
    },

    missingChoicesMessage() {
      const count = this.missingGroupIds.length
      if (count === 0) return ''
      return count === 1 ? 'Please make the required choice above' : 'Please make the required choices above'
    },

    ctaLabel() {
      if (this.orderingClosed) return 'Ordering closed'
      if (this.isRemoving) return 'Remove from cart'
      return this.editing ? 'Update cart' : 'Add to cart'
    },

    quantityUnitLabel() {
      if (!this.product) return null
      return this.quantity === 1 ? this.product.quantityUnit : this.product.quantityUnitPlural
    }
  },

  watch: {
    open: {
      immediate: true,
      handler(isOpen) {
        if (isOpen) this.reset()
      }
    },

    // Re-opening on a different product while already open must not carry the previous selection.
    'product.slug'() {
      if (this.open) this.reset()
    }
  },

  methods: {
    reset() {
      this.selection = this.initialSelection ? JSON.parse(JSON.stringify(this.initialSelection)) : {}
      // Through the same rule the stepper enforces, so a modal opened on a line the backend has
      // already rounded up opens on the number the cart is actually holding.
      this.quantity = snapQuantity(Math.max(this.startingQuantity, Number(this.initialQuantity) || 0), {
        step: this.product && this.product.quantityStep,
        floor: this.startingQuantity
      })
      this.collapsedGroupIds = []
      this.addRefused = false
      this.nudgedGroupId = null
    },

    isMissing(group) {
      return this.missingGroupIds.includes(group.id)
    },

    isExpanded(group) {
      return !this.collapsedGroupIds.includes(group.id)
    },

    toggleGroup(group) {
      if (this.isExpanded(group)) {
        this.collapsedGroupIds = [...this.collapsedGroupIds, group.id]
      } else {
        this.collapsedGroupIds = this.collapsedGroupIds.filter((id) => id !== group.id)
      }
    },

    // The heading has to describe what the group actually allows. It used to say "Please choose 1"
    // for every required group regardless of mode, so a multi-select or quantity group invited a
    // single choice and then accepted several.
    groupSubtitle(group) {
      // An optional group reads "Optional", matching the prototype's "No Cheese?".
      if (!group.required) return group.mode === QUANTITY && group.max ? `Optional - up to ${group.max}` : 'Optional'
      if (group.mode === SELECT_ONE) return 'Please choose 1'

      const { min, max } = group
      if (min && max) return min === max ? `Please choose ${min}` : `Choose between ${min} and ${max}`
      if (min) return `Choose at least ${min}`
      if (max) return `Choose up to ${max}`
      return 'Choose at least 1'
    },

    isSelected(group, option) {
      return Boolean(this.selection[group.id] && this.selection[group.id][option.id])
    },

    quantityFor(group, option) {
      return (this.selection[group.id] && this.selection[group.id][option.id]) || 0
    },

    groupTotal(group) {
      return groupSelectionTotal(group, this.selection)
    },

    // group.limit caps the total across the group, not each option on its own -
    // V1's inline custom-quantity rows disabled "+" once the summed quantity for the variant
    // reached its maximum, and capped each option at its own remaining inventory. Handing
    // the cap straight to every stepper let a five-option group of max 10 reach 50.
    optionMax(group, option) {
      const budget =
        group.limit == null
          ? Infinity
          : Math.max(group.limit - (this.groupTotal(group) - this.quantityFor(group, option)), 0)
      const stock = option.inventoryRemaining == null ? Infinity : option.inventoryRemaining
      const limit = Math.min(budget, stock)
      return limit === Infinity ? null : limit
    },

    optionPriceLabel(option) {
      if (!this.product.showPrices || !option.deltaCents) return ''
      const sign = option.deltaCents > 0 ? '+' : '-'
      return `${sign}${formatPrice(Math.abs(option.deltaCents), this.currency)}`
    },

    chooseOption(group, option) {
      if (option.soldOut) return

      // A single-choice group replaces its selection outright; this is the same semantics V1 gets
      // by filtering out siblings sharing a variant_id on its inline options.
      if (group.mode === SELECT_ONE) {
        const cleared = !group.required && this.isSelected(group, option)
        this.selection = { ...this.selection, [group.id]: cleared ? {} : { [option.id]: 1 } }
        return
      }

      const current = { ...(this.selection[group.id] || {}) }
      if (current[option.id]) {
        delete current[option.id]
      } else {
        if (group.max && this.groupTotal(group) >= group.max) return
        current[option.id] = 1
      }
      this.selection = { ...this.selection, [group.id]: current }
    },

    setOptionQuantity(group, option, value) {
      const limit = this.optionMax(group, option)
      const next = limit == null ? value : Math.min(value, limit)
      const current = { ...(this.selection[group.id] || {}) }
      if (next > 0) {
        current[option.id] = next
      } else {
        delete current[option.id]
      }
      this.selection = { ...this.selection, [group.id]: current }
    },

    add() {
      if (this.orderingClosed) return
      if (!this.canAdd) {
        this.addRefused = true
        this.revealFirstUnsatisfiedGroup()
        return
      }
      this.$emit('add', {
        selection: JSON.parse(JSON.stringify(this.selection)),
        quantity: this.quantity,
        totalCents: this.totalCents
      })
    },

    // `unsatisfiedGroups` filters `modifierGroups`, so it keeps display order and [0] is the first
    // thing standing between the customer and the cart. Sending them to the topmost one rather than
    // flagging all of them means the button always has one clear next step to point at.
    revealFirstUnsatisfiedGroup() {
      const group = this.unsatisfiedGroups[0]
      if (!group) return

      // Scrolling to a collapsed group lands on a header with no options under it, which reads as
      // the button having done nothing.
      this.collapsedGroupIds = this.collapsedGroupIds.filter((id) => id !== group.id)

      // A second press with the group already on screen and already red would otherwise change
      // nothing visible. The nudge clears itself on `animationend`, so every press replays it.
      this.nudgedGroupId = group.id

      this.$nextTick(() => {
        const body = this.$refs.body
        const section = body && body.querySelector(`[data-testid="modifier-group-${group.id}"]`)
        if (!section) return

        // Absent in jsdom, and older Safari has no smooth behaviour - either way the focus below
        // still brings the group into view.
        if (typeof section.scrollIntoView === 'function') {
          section.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }

        // A scroll says nothing to a screen reader, and the press moved focus nowhere. `preventScroll`
        // so this does not jump the smooth scroll straight to the end.
        const header = section.querySelector(`[data-testid="modifier-group-header-${group.id}"]`)
        if (header && typeof header.focus === 'function') header.focus({ preventScroll: true })
      })
    }
  }
}
</script>

<style scoped>
/* On the heading text rather than the whole bar: the bar is full width, so shifting it would
   overflow the scroll body and flash a horizontal scrollbar. */
.required-nudge {
  animation: required-nudge 0.4s ease-in-out;
}
@keyframes required-nudge {
  0%,
  100% {
    transform: translateX(0);
  }
  20%,
  60% {
    transform: translateX(-4px);
  }
  40%,
  80% {
    transform: translateX(4px);
  }
}
@media (prefers-reduced-motion: reduce) {
  .required-nudge {
    animation: none;
  }
}
</style>
